import { useEffect, useState, useCallback, RefObject } from "react";
import { resolveDropdownPlacement } from "components/Select/selectPlacement";

interface MenuPosition {
  top: number;
  left: number;
  ready: boolean;
}

interface UseMenuPositionOptions {
  /**
   * "auto" (default): left-align menu with button, prefer opening above.
   * "right": right-align menu's right edge with button's right edge, prefer opening below.
   *          Y position is locked once computed (does not recalculate on menu content size changes).
   */
  placement?: "auto" | "right";
}

export const useMenuPosition = (showMenu: boolean, downloadButtonRef: RefObject<HTMLDivElement>, menuRef: RefObject<HTMLDivElement>, options: UseMenuPositionOptions = {}) => {
  const { placement = "auto" } = options;
  const [menuStyle, setMenuStyle] = useState<MenuPosition>({ top: 0, left: 0, ready: false });

  const calculateMenuPosition = useCallback(
    (lockedTop?: number) => {
      if (!showMenu || !downloadButtonRef.current || !menuRef.current) return;

      const buttonRect = downloadButtonRef.current.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();
      const menuWidth = 270;
      const menuHeight = menuRect.height;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const gap = 8;
      const padding = 10;

      // Horizontal positioning
      let calculatedLeft: number;
      if (placement === "right") {
        // Align right edge of menu with right edge of button
        calculatedLeft = buttonRect.right - menuWidth;
      } else {
        // Default: align left edge of menu with left edge of button
        calculatedLeft = buttonRect.left;
      }

      // Overflow guards
      if (calculatedLeft + menuWidth > viewportWidth - padding) {
        calculatedLeft = viewportWidth - menuWidth - padding;
      }
      if (calculatedLeft < padding) {
        calculatedLeft = padding;
      }

      // Vertical positioning
      let calculatedTop: number;
      if (lockedTop !== undefined) {
        // Stable Y — don't recalc when content size changes
        calculatedTop = lockedTop;
      } else {
        // Which side goes through the one shared rule (REF-6158). Both branches this replaced
        // flipped as soon as the preferred side ran short, without asking whether the other side
        // had MORE room. It never escaped the viewport, because the clamps below drag it back in
        // — but "dragged back in" means the menu covers its own button, and the reasoning was the
        // one that produced four tickets. "auto" prefers above, "right" prefers below.
        const { openAbove } = resolveDropdownPlacement({
          spaceBelow: viewportHeight - buttonRect.bottom,
          spaceAbove: buttonRect.top,
          contentHeight: menuHeight,
          preferAbove: placement !== "right",
        });

        calculatedTop = openAbove ? buttonRect.top - menuHeight - gap : buttonRect.bottom + gap;
      }

      // Bottom overflow clamp
      if (calculatedTop + menuHeight > viewportHeight - padding) {
        calculatedTop = viewportHeight - menuHeight - padding;
      }
      if (calculatedTop < padding) {
        calculatedTop = padding;
      }

      setMenuStyle({ top: calculatedTop, left: calculatedLeft, ready: true });
    },
    [showMenu, downloadButtonRef, menuRef, placement]
  );

  useEffect(() => {
    if (!showMenu) {
      setMenuStyle({ top: 0, left: 0, ready: false });

      return;
    }

    let lockedTop: number | undefined;
    let observerRef: ResizeObserver | undefined;

    // Use two rAF frames to ensure the portal menu is in the DOM and painted
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        calculateMenuPosition();

        if (placement === "right") {
          // Lock the Y position from this initial calculation; subsequent ResizeObserver fires
          // (e.g., menu content swap) reuse the same Y so the menu doesn't jump.
          if (menuRef.current) {
            const buttonRect = downloadButtonRef.current?.getBoundingClientRect();
            if (buttonRect) lockedTop = buttonRect.bottom + 8;
          }
        }

        // Observe menu size changes after it's in the DOM
        if (menuRef.current && !observerRef) {
          observerRef = new ResizeObserver(() => {
            calculateMenuPosition(lockedTop);
          });
          observerRef.observe(menuRef.current);
        }
      });
    });

    const handleScrollOrResize = () => calculateMenuPosition(lockedTop);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      cancelAnimationFrame(rafId);
      observerRef?.disconnect();
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [showMenu, calculateMenuPosition, menuRef, downloadButtonRef, placement]);

  return menuStyle;
};
